import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IIntegrationUIMapping } from '@feathr/components';
import { toast } from '@feathr/components';

import type {
  ICrmContactMapping,
  ICrmContactMappingUpdate,
  ICrmMappingRules,
  IMappingChanges,
} from './crmMappings.utils';
import {
  getAddedMappingChanges,
  getMappingChanges,
  hasMappingChanges,
  lockManagedFeathrFields,
  orderMappingUpdates,
  toMappingUpdate,
  toUIMapping,
} from './crmMappings.utils';

/** The contact mapping store an integration exposes. */
export interface ICrmMappingStore<TMapping extends ICrmContactMapping> {
  sortedMappings: TMapping[];
  isFetched: boolean;
  isPending: boolean;
  error: Error | null;
  fetch(): Promise<void>;
  refresh(): Promise<void>;
  add(keys: Array<TMapping['key']>): Promise<TMapping[]>;
  update(change: ICrmContactMappingUpdate): Promise<TMapping | null>;
  remove(id: string): Promise<void>;
}

export interface ICrmMappingIntegration<TMapping extends ICrmContactMapping> {
  id: string;
  contactMappingStore: ICrmMappingStore<TMapping>;
}

export interface ICrmMappingDraft {
  /** The table rows: the staged edits, or the saved mappings when nothing is staged. */
  rows: readonly IIntegrationUIMapping[];
  /** Stages the table's new rows. */
  setRows: (rows: IIntegrationUIMapping[]) => void;
  /** Whether the staged rows differ from the saved mappings. */
  isDirty: boolean;
  /** Why the staged rows can't be saved yet. */
  saveDisabledReason?: string;
  /** Saves the staged rows, then drops them for the reloaded mappings. Rejects with the first failure. */
  save: () => Promise<void>;
}

/** Runs the requests one at a time. Every request runs even if one fails; failures are collected. */
function runInSequence(
  requests: ReadonlyArray<() => Promise<unknown>>,
  failures: unknown[],
): Promise<void> {
  return requests.reduce<Promise<void>>(
    (previous, request) =>
      previous.then(request).then(
        () => undefined,
        (error: unknown) => {
          failures.push(error);
        },
      ),
    Promise.resolve(),
  );
}

/**
 * Applies the changes one request at a time, in phase order, so no request takes a Feathr field
 * that another still holds. Every request runs even if one fails; the first failure is rethrown.
 */
async function applyMappingChanges<TMapping extends ICrmContactMapping>(
  store: ICrmMappingStore<TMapping>,
  saved: readonly TMapping[],
  changes: IMappingChanges<TMapping['key']>,
  serverBoundKeys: ReadonlySet<string> | undefined,
): Promise<void> {
  const failures: unknown[] = [];
  const created: TMapping[] = [];
  const update = (change: ICrmContactMappingUpdate) => (): Promise<unknown> => store.update(change);
  const clear = (id: string): (() => Promise<unknown>) => update(toMappingUpdate(id, null));

  await runInSequence(
    [
      ...changes.remove.map((id) => (): Promise<unknown> => store.remove(id)),
      ...changes.clear.map(clear),
      ...changes.add.map(({ key }) => async (): Promise<void> => {
        created.push(...(await store.add([key])));
      }),
    ],
    failures,
  );
  const added = getAddedMappingChanges(created, changes, serverBoundKeys);
  const assign = orderMappingUpdates([...added.assign, ...changes.assign], [...saved, ...created]);
  // Direction-only updates keep their Feathr field, so they need no ordering.
  await runInSequence(
    [...added.clear.map(clear), ...assign.map(update), ...changes.syncRule.map(update)],
    failures,
  );
  if (failures.length) {
    throw failures[0];
  }
}

/**
 * Mapping edits staged for the page's Save & Sync. Reads the integration's mapping store; the
 * Mappings tab owns its load. Lives on the page so staged edits survive a tab switch.
 */
export function useCrmMappingDraft<TMapping extends ICrmContactMapping>(
  integration: ICrmMappingIntegration<TMapping> | undefined,
  crmName: string,
  rules: ICrmMappingRules<TMapping> = {},
): ICrmMappingDraft {
  const { t } = useTranslation();
  // Staged rows belong to one integration, so a replaced model doesn't inherit them.
  const [draft, setDraft] = useState<{ integrationId: string; rows: IIntegrationUIMapping[] }>();
  const store = integration?.contactMappingStore;
  const saved = store?.sortedMappings ?? [];
  const stagedRows = draft && draft.integrationId === integration?.id ? draft.rows : undefined;
  const rows = lockManagedFeathrFields(
    stagedRows ?? saved.map((mapping) => toUIMapping(mapping, t, rules)),
    saved,
    rules,
  );
  const changes = getMappingChanges(saved, rows, rules.serverBoundKeys);
  const isDirty = !!stagedRows && hasMappingChanges(changes);

  function getSaveDisabledReason(): string | undefined {
    if (!isDirty) {
      return undefined;
    }
    if (rows.some(({ crmFieldKey }) => crmFieldKey === null)) {
      return t('Choose a {{crmName}} field for each new mapping, or remove it.', { crmName });
    }
    if (rules.requireFeathrField && rows.some(({ feathrFieldKey }) => feathrFieldKey === null)) {
      return t('Choose a Feathr field for each mapping, or remove it.');
    }
    return undefined;
  }

  function setRows(nextRows: IIntegrationUIMapping[]): void {
    if (integration) {
      setDraft({ integrationId: integration.id, rows: nextRows });
    }
  }

  async function save(): Promise<void> {
    if (!store || !isDirty) {
      return;
    }
    try {
      await applyMappingChanges(store, saved, changes, rules.serverBoundKeys);
    } finally {
      // The store reloads after every change, so it holds whatever the server saved.
      setDraft(undefined);
    }
  }

  return { isDirty, rows, save, saveDisabledReason: getSaveDisabledReason(), setRows };
}

/**
 * Reloads the saved mappings on each poll, so each row's last sync stays current. Staged edits are
 * kept. Skips a store the Mappings tab has not loaded yet.
 */
export function useCrmMappingsRefresh<TMapping extends ICrmContactMapping>(
  integration: ICrmMappingIntegration<TMapping> | undefined,
  pollCount: number,
): void {
  useEffect(() => {
    const store = integration?.contactMappingStore;
    if (pollCount && store?.isFetched) {
      void store.refresh();
    }
  }, [pollCount]);
}

/**
 * Loads (or reloads) the integration's contact mapping store on mount and returns it. Call it from
 * one owner component per page; others read `integration.contactMappingStore` from an `observer`.
 */
export function useCrmContactMappings<
  TMapping extends ICrmContactMapping,
  TStore extends ICrmMappingStore<TMapping>,
>(integration: { contactMappingStore: TStore }): TStore {
  const { t } = useTranslation();
  const store = integration.contactMappingStore;

  useEffect(() => {
    let isMounted = true;

    async function load(): Promise<void> {
      await (store.isFetched ? store.refresh() : store.fetch());
      if (isMounted && !store.isFetched && store.error) {
        toast(t("Couldn't load mappings. {{error}}", { error: store.error }), { type: 'error' });
      }
    }

    void load();

    return (): void => {
      isMounted = false;
    };
    // `t` is left out on purpose: a language change must not reload the mappings.
  }, [store]);

  return store;
}
